import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import { useGetMealPaymentRefundListQuery } from '../../services/services';
import { RootState } from '../../../../store/store';
import { formatAndDisplayValue } from '../../../../config/formatAndDisplayValue';
import Table from '../../../../components/table/Table';
import { filterOperatorsDateTime } from '../../../../utils/filter-date-time';
import { mapReactFilterValueTypeToRequestFilterValueType, mapReactMUIToFilterOperationTypes } from '../../../../utils/RequestFilterEnums';

const RefundHistoryModal = () => {
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  
  // Request body structure similar to MealCardsList
  const [requestBody, setRequestBody] = useState({
    filters: [],
    pageSize: 25,
    page: 1,
    orderBy: "cDate", // Default sort by creation date
    orderDesc: true, // Default descending order (newest first)
    customerId: currentUserState?.companyId || currentUserState?.customerId || 0,
    companyId: currentUserState?.companyId || currentUserState?.customerId || 0,
  });

  // Kurum değişince requestBody customer/company id senkronlanmazsa refetch kaçabilir
  useEffect(() => {
    const nextId = currentUserState?.companyId || currentUserState?.customerId || 0;
    if (!nextId) return;
    setPaginationModel({ page: 0, pageSize: 25 });
    setRequestBody((prev) => ({
      ...prev,
      filters: [],
      pageSize: 25,
      page: 1,
      orderBy: "cDate",
      orderDesc: true,
      customerId: nextId,
      companyId: nextId,
    }));
  }, [currentUserState?.companyId, currentUserState?.customerId]);

  // API'den iade geçmişi verilerini al
  const { data: refundData, isLoading } = useGetMealPaymentRefundListQuery({
    filters: requestBody.filters,
    pageSize: requestBody.pageSize,
    page: requestBody.page,
    orderBy: requestBody.orderBy,
    orderDesc: requestBody.orderDesc,
    customerId: requestBody.customerId,
    companyId: requestBody.companyId,
  }, { 
    skip: !requestBody.customerId,
    refetchOnMountOrArgChange: true
  });

  const baseColumns: GridColDef[] = [
    // @ts-ignore
    { field: 'cDate', headerName: 'Oluşturma Tarihi', width: 180, type: 'datetime', filterOperators: filterOperatorsDateTime },
    { field: 'customerName', headerName: 'Ad Soyad', width: 200 },
    { field: 'cardNo', headerName: 'Kart Numarası', width: 250 },
    { field: 'senderWalletNo', headerName: 'Kart ID', width: 200 },
    { field: 'value', headerName: 'İade Tutarı', width: 180, valueFormatter: (value) => formatAndDisplayValue(value || 0, 'TRY') }
  ];

  const sortModelChange = (model: any) => {
    if (model?.length) {
      setRequestBody((prev) => ({ 
        ...prev, 
        orderBy: model[0].field, 
        orderDesc: model[0].sort === "asc" // MealCardsList ile aynı mantık
      }));
    } else {
      setRequestBody((prev) => ({ 
        ...prev, 
        orderBy: "", 
        orderDesc: false 
      }));
    }
  };

  // localStorage temizleme kaldırıldı - filtrelerin input'ta görünmesi için
  // Table component'i localStorage'dan filterModel'i otomatik olarak yükleyecek

  // Table -> server filter conversion (DataGridPro filterModel -> bizim GET yapısı)
  const convertTableFiltersToRequestFilter = (tableFilters: any) => {
    let converted: any[] = [];
    const items = tableFilters || [];
    items.forEach((item: any) => {
      if (item.value !== undefined && item.value !== null && item.value !== '') {
        const findColumn = baseColumns.find(c => c.field === item.field);
        converted.push({
          field: item.field,
            logicOperator: 'and',
            values: [item.value],
            valueType: mapReactFilterValueTypeToRequestFilterValueType(item.value, (findColumn as any)?.type),
            operationType: mapReactMUIToFilterOperationTypes(item.operator)
        });
      }
    });
    return converted;
  };

  // Debounce function similar to MealCardsList
  const debounce = (func: any, delay: any) => {
    let timeoutId: any;
    return (...args: any) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // MealCardsList ile aynı yapıda filter handling
  const onFilterChange = debounce((filterObj: any) => {
    setRequestBody((prev) => ({ 
      ...prev, 
      filters: filterObj,
      page: 1 // yeni filtrede başa dön
    }));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, 500);

  const handleFilterChange = useCallback((filterItems: any) => {
    // filterItems zaten Table içindeki convert edilmiş dizi olabilir; yoksa dönüştür.
    const filtersArr = Array.isArray(filterItems) ? filterItems : convertTableFiltersToRequestFilter(filterItems);
    onFilterChange(filtersArr);
  }, [onFilterChange]);

  const handlePaginationChange = (model: any) => {
    setPaginationModel(model);
    setRequestBody((prev) => ({ 
      ...prev, 
      pageSize: model.pageSize, 
      page: model.page + 1 
    }));
  };

  // Veri yoksa bile tabloyu gizleme, sadece "Kayıt Bulunamadı" mesajını Table içinde göster
  return (
    <Box sx={{ p: 3 }}>
      <Table
          sortModelChange={sortModelChange}
          loading={isLoading}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
          checkboxSelection={true}
          headerFilters={true}
          showDownloadButton={true}
          columnVisibilityConfigKey="refundHistoryFilterColumnVisibilityKey"
          filterKey="refundHistoryFilter"
          rows={refundData?.data || []}
          columns={baseColumns}
          isLoading={isLoading}
          handleFilterChange={handleFilterChange}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          rowCount={refundData?.totalCount || refundData?.data?.length || 0}
          getRowId={(row: any) => row.id || row.transactionId || row.refundId || `${row.cardNo}-${row.cDate}-${row.value}`}
          idName={'id'}
          listUrl={'/Process/GetMealPaymentRefundListV2'}
          fetchMethod="POST"
          fetchData={refundData}
          listName="İade_Geçmişi"
          customerId={requestBody.customerId}
          companyId={requestBody.companyId}
          requestBody={requestBody}
          sx={{
            '& .MuiDataGrid-root': { borderRadius: 2, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
            '& .MuiDataGrid-main': { borderRadius: 2 },
            '& .MuiDataGrid-columnHeaders': { background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.95) 100%)', borderBottom: '2px solid rgba(226, 232, 240, 0.8)', borderRadius: '8px 8px 0 0' },
            '& .MuiDataGrid-cell': { borderBottom: '1px solid rgba(226, 232, 240, 0.5)' },
            '& .MuiDataGrid-row': { '&:hover': { background: 'rgba(139, 92, 246, 0.04)' } },
          }}
        />
    </Box>
  );
};

export default RefundHistoryModal;
